import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import CollecteSuccessMessage from "../components/CollecteSuccessMessage";
import CollecteErrorMessage from "../components/CollecteErrorMessage";
import "../styles/UserFormPage.css";
import { User } from "../types/User";
import { userService } from "../services/UserService";
import { roleService } from "../services/RoleService";
import { Role } from "../types/Role";
import { useAuth } from "../context/AuthContext";
import CollecteLoadingMessage from "../components/CollecteLoadingMessage";
import AuthentifiedPage from "./AuthentifiedPage";

const UserFormPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();


  const stateUser = location.state?.user;
  const { api } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [user, setUser] = useState<User>({
    id: 0,
    nom: "",
    prenom: "",
    telephone: "",
    role: "technicien",
    statut: "actif",
    email: "",
    adresse: "",
    username: "",
    password: "",
  });


  useEffect(() => {
    const loadUser = async () => {
      if (stateUser) {
        setUser(stateUser);
        return;
      }

      if (id && api) {
        try {
          const fetched = await userService.getUserById(api, id);
          if (fetched) setUser(fetched);
        } catch (error) {
          console.error("Erreur lors de la récupération de l'utilisateur :", error);
        }
      }
    };

    loadUser();
  }, [stateUser, id, api]);

  useEffect(() => {
    const loadRoles = async () => {
      if (!api) return;
      setLoadingRoles(true);
      try {
        const rolesData = await roleService.getAllRoles(api);
        setRoles(rolesData);

        // Si user.role = string, transformer en id
        if (user && typeof user.role === 'string' && rolesData.length > 0) {
          const maybeId = Number(user.role);
          if (isNaN(maybeId)) {
            const roleLabel = (user.role as string).toLowerCase();
            const found = rolesData.find(r => (r.label || '').toLowerCase() === roleLabel);
            if (found) setUser(prev => ({ ...prev, role: String(found.id) }));
          }
        }
      } catch (err) {
        console.error('Erreur lors du chargement des rôles :', err);
      } finally {
        setLoadingRoles(false);
      }
    };

    loadRoles();
  }, [api]);

  useEffect(() => {
    if (!roles || roles.length === 0) return;
    if (!user || typeof user.role !== 'string') return;

    const maybeId = Number(user.role);
    if (!isNaN(maybeId)) return;

    const roleLabel = (user.role as string).toLowerCase();
    const found = roles.find(r => (r.label || '').toLowerCase() === roleLabel);
    if (found) {
      setUser(prev => ({ ...prev, role: String(found.id) }));
    }
  }, [roles, user.role]);

  const getStatutValue = (): string => {
    if (typeof user.statut === "boolean") return user.statut ? "actif" : "inactif";
    return (user.statut as string) || "actif";
  };

  const [successVisible, setSuccessVisible] = useState(false);
  const [errorVisible, setErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const currentUser: User = {
    id: 1,
    nom: "Admin",
    prenom: "admin",
    email: "admin@example.com",
    telephone: "0340000000",
    role: "technicien",
    statut: "actif",
    username: "Admin",
    adresse: "",
    password: "admin123",
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
  };

  const genererMotDePasse = () => {
    const newPassword = Math.random().toString(36).slice(-8);
    setUser((prev) => ({ ...prev, password: newPassword }));
    alert(`Mot de passe généré: ${newPassword}`);
  };

  const handleSubmit = async () => {
    const isCreate = !user.id || user.id === 0;
    if (!user.nom || !user.username || (isCreate && !user.password)) {
      setErrorVisible(true);
      setErrorMessage("Veuillez remplir tous les champs requis.");
      setTimeout(() => setErrorVisible(false), 2000);
      return;
    }

    setLoading(true);
    try {
      // Payload
      const payload: any = {
        firstName: user.prenom || "",
        lastName: user.nom,
        email: user.email || "",
        phoneNumber: user.telephone || "",
        username: user.username,
        address: user.adresse || "",
        role: { roleId: Number(user.role) || undefined },
        isActive: typeof user.statut === "boolean" ? user.statut : (user.statut === "actif"),
      };

      // N'envoyer le mot de passe que s'il a été saisi
      if (user.password) payload.password = user.password;

      if (user.id && user.id !== 0) {
        await userService.updateUser(api, user.id, payload);
      } else {
        await userService.createUser(api, payload);
      }

      setErrorMessage("");
      setSuccessVisible(true);
      setTimeout(() => {
        setSuccessVisible(false);
        navigate("/admin");
      }, 1500);
    } catch (error: any) {
      console.error("Erreur lors de l'enregistrement via API :", error);
      const errorMsg = "Une erreur est survenue lors de l'enregistrement.";
      setErrorMessage(errorMsg);
      setErrorVisible(true);
      setTimeout(() => setErrorVisible(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin"); // Retour à la liste
  };

  const handleDesactiver = () => {
    if (window.confirm("Voulez-vous vraiment désactiver ce compte ?")) {
      setUser((prev) => ({ ...prev, statut: "inactif" }));
      alert("Compte désactivé !");
    }
  };

  return (
    <AuthentifiedPage
      leftButtons={[
        { text: "Annuler", color: "secondary", onClick: handleCancel },
      ]}
      rightButton={{ text: "Enregistrer les modifications", color: "green", onClick: handleSubmit }}
    >
      <CollecteLoadingMessage visible={loading} message="Enregistrement en cours..." />

      <CollecteSuccessMessage
        visible={successVisible}
        message="Utilisateur enregistré avec succès"
      />
      <CollecteErrorMessage
        visible={errorVisible}
        message={errorMessage || "Erreur : Veuillez remplir tous les champs requis"}
      />

      <main className="container py-4 flex-grow-1">
        <h4 className="text-success mb-1">
          Fiche utilisateur : <span className="fw-bold">{user.nom || "Nouveau"}</span>
        </h4>
        <p className="text-muted">
          Complétez les informations de l'utilisateur avant d'enregistrer.
        </p>

        <form className="user-form mt-4">
          <div className="bg-white rounded shadow-sm p-4">
            <h6 className="fw-semibold mb-3 text-dark">Informations sur l’utilisateur</h6>

            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <input
                  type="text"
                  name="nom"
                  value={user.nom}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Nom"
                />
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  name="prenom"
                  value={user.prenom}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Prénoms"
                />
              </div>

              <div className="col-md-6">
                <input
                  type="email"
                  name="email"
                  value={user.email}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Email"
                />
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  name="telephone"
                  value={user.telephone}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Téléphone"
                  />
              </div>

              <div className="col-md-6">
                <input 
                  type="text" 
                  name="adresse" 
                  value={user.adresse}
                  onChange={handleChange}
                  className="form-control" 
                  placeholder="Adresse" />
              </div>

              <div className="col-md-6">
                <select
                  name="statut"
                  className="form-select"
                  value={getStatutValue()}
                  onChange={handleChange}
                >
                  <option value="actif">Actif</option>
                  <option value="inactif">Inactif</option>
                </select>
              </div>
            </div>

            <h6 className="fw-semibold mb-3 text-dark">Authentification</h6>
            <div className="row g-3 align-items-center mb-4">
              <div className="col-md-4">
                <input
                  type="text"
                  name="username"
                  value={user.username}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Nom d'utilisateur"
                />
              </div>
              <div className="col-md-4">
  <div className="input-group password-input-group">
    <input
      type={showPassword ? "text" : "password"}
      name="password"
      value={user.password}
      onChange={handleChange}
      className="form-control"
      placeholder="Mot de passe"
      aria-label="Mot de passe"
    />

    <span
      className="input-group-text password-toggle"
      role="button"
      onClick={() => setShowPassword(!showPassword)}
      aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
    >
      <i className={showPassword ? "bi bi-eye-slash" : "bi bi-eye"}></i>
    </span>
  </div>
</div>

              <div className="col-md-4">
                <button
                  className="btn btn-success w-100"
                  type="button"
                  onClick={genererMotDePasse}
                >
                  Générer le mot de passe
                </button>
              </div>
            </div>

            <h6 className="fw-semibold mb-3 text-dark">Rôles</h6>
            <div className="col-md-6 mb-3">
              <select
                className="form-select"
                name="role"
                value={user.role}
                onChange={handleChange}
                disabled={roles.length === 0}
              >
                <option value="">Séléctionner un rôle</option>
                {roles.length > 0 ? (
                  roles.map(r => (
                    <option key={r.id} value={String(r.id)}>
                      {r.label}
                    </option>
                  ))
                ) : (
                  <>
                  </>
                )}
              </select>
            </div>
          </div>
        </form>
      </main>
    </AuthentifiedPage>
  );
};

export default UserFormPage;
